import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { of, pipe } from 'rxjs';
import { catchError, finalize, first, switchMap, tap } from 'rxjs/operators';

import { RegistrationErrorDialogComponent } from '../../pages/entry/registration/registration-error-dialog/registration-error-dialog.component';

import { initialRegistrationState, RegistrationState } from '../models/state/registration.state';
import { AuthService, DialogService, PartnerService } from '../services';
import { Partner, RegisterCustomerData } from '../models';
import { LOGIN_PATH } from '../constants';
import { RegistrationErrorType } from '../enums';

export const RegistrationStore = signalStore(
  withState<RegistrationState>(initialRegistrationState),
  withMethods(
    (
      store,
      partnerService = inject(PartnerService),
      authService = inject(AuthService),
      dialogService = inject(DialogService),
      toastrService = inject(ToastrService),
      translateService = inject(TranslateService),
      router = inject(Router),
    ) => {
      const loadManager = rxMethod<string | undefined>(
        pipe(
          tap(() => patchState(store, { managerLoading: true })),
          switchMap((qrCodeId) =>
            (qrCodeId ? partnerService.getPartnerByQrCodeId(qrCodeId) : of({} as Partner)).pipe(
              tap((manager) => patchState(store, { manager: manager.name ? manager : undefined })),
              catchError(() => of()), // Prevent rxMethod from crashing
              finalize(() => patchState(store, { managerLoading: false })),
            ),
          ),
        ),
      );
      const register = rxMethod<RegisterCustomerData>(
        pipe(
          tap(() => patchState(store, { loading: true })),
          switchMap((data) =>
            authService.register(data).pipe(
              tap(() => {
                toastrService.success(translateService.instant('registration.alerts.registrationSuccess'));
                router.navigate([LOGIN_PATH]).then();
              }),
              catchError((error) => {
                if (![409, 428].includes(error.status)) {
                  const errorKey =
                    error.status === 400 ? 'common.alerts.missingFieldsError' : 'common.alerts.genericError';
                  toastrService.error(translateService.instant(errorKey));
                  return of(); // Prevent rxMethod from crashing
                }
                const errorType = RegistrationErrorType.fromErrorCode(error.status);
                return dialogService
                  .openDialog(RegistrationErrorDialogComponent, { errorType })
                  .beforeClosed()
                  .pipe(
                    first(),
                    tap((doAction) => {
                      if (!doAction) {
                        return;
                      }
                      if (errorType === RegistrationErrorType.BoxOffline) {
                        register(data);
                      } else {
                        router.navigate([LOGIN_PATH]).then();
                      }
                    }),
                  );
              }),
              finalize(() => patchState(store, { loading: false })),
            ),
          ),
        ),
      );

      return { loadManager, register };
    },
  ),
);
